import instance from "lib/axiosInterceptor";
import {
  GetReportDocumentVisitsServiceResponse,
  GetReportDocumentAnalyticsServiceResponse,
  GetReportDocumentUsageServiceResponse,
  GetReportDashboardServiceResponse,
  GetPresentationPlaylistResponse,
  GetReportDownloadsResponse,
  GetReportPresentationEngagementParams,
  GetReportPresentationEngagementResponse,
  GetReportPresentationHighestIntentAudienceParams,
  GetReportPresentationHighestIntentAudienceResponse,
  GetReportPresentationParticipantDeepDiveParams,
  GetReportPresentationParticipantDeepDiveResponse,
  GetReportPresentationAnalyticsOverviewResponse,
  GetReportPresentationAssetPerformanceResponse,
  GetReportPresentationPerformanceInsightParams,
  GetReportPresentationPerformanceInsightResponse,
  GetReportPresentationPerformanceScoreResponse,
  GetReportPresentationPillarsParams,
  GetReportPresentationPillarsResponse,
  GetReportPresentationRoiConversionParams,
  GetReportPresentationRoiConversionResponse,
  GetReportPresentationSessionPerformanceParams,
  GetReportPresentationSessionPerformanceResponse,
  GetReportUserAttentionResponse,
  GetReportPerformanceSummaryResponse,
  GetReportAttendancePresentationResponse,
  GetReportPresentationMetricsResponse,
  GetReportAverageTimeSpentResponse,
} from "interfaces";

export async function getReportDocumentVisitsService(
  documentId: string
): Promise<GetReportDocumentVisitsServiceResponse> {
  const response = await instance.get<GetReportDocumentVisitsServiceResponse>(
    `/Report/Document/Visits/${documentId}`
  );
  return response.data;
}

export async function getReportDocumentAnalyticsService(
  documentId: string
): Promise<GetReportDocumentAnalyticsServiceResponse> {
  const response =
    await instance.get<GetReportDocumentAnalyticsServiceResponse>(
      `/Report/Document/Analytics/${documentId}`
    );
  return response.data;
}

export async function getReportDocumentUsageService(
  documentId: string
): Promise<GetReportDocumentUsageServiceResponse> {
  const response = await instance.get<GetReportDocumentUsageServiceResponse>(
    `/Report/Document/Usage/${documentId}`
  );
  return response.data;
}

export async function getReportDashboardService(): Promise<GetReportDashboardServiceResponse> {
  const response = await instance.get<GetReportDashboardServiceResponse>(
    `/report/dashboard`
  );
  return response.data;
}

export async function getReportPresentationPlaylistService(
  presentationId: string
): Promise<GetPresentationPlaylistResponse[]> {
  const response = await instance.get<GetPresentationPlaylistResponse[]>(
    `/Report/Presentation/Playlist/${presentationId}`
  );
  return response.data;
}

export async function getReportDownloadsService(
  presentationId: string
): Promise<GetReportDownloadsResponse> {
  const response = await instance.get<GetReportDownloadsResponse>(
    `/Report/Downloads/${presentationId}`
  );
  return response.data;
}

export async function getReportUserAttentionService(
  presentationId: string
): Promise<GetReportUserAttentionResponse> {
  const response = await instance.get<GetReportUserAttentionResponse>(
    `/Report/UserAttention/${presentationId}`
  );
  return response.data;
}

export async function getReportPresentationEngagementService(
  params: GetReportPresentationEngagementParams
): Promise<GetReportPresentationEngagementResponse> {
  const response = await instance.get<GetReportPresentationEngagementResponse>(
    `/Report/Presentation/Analytics/Engagement`,
    {
      params,
    }
  );
  return response.data;
}

export async function getReportPresentationPillarsService(
  params: GetReportPresentationPillarsParams
): Promise<GetReportPresentationPillarsResponse> {
  const response = await instance.get<GetReportPresentationPillarsResponse>(
    `/Report/Presentation/Analytics/Pillars`,
    {
      params,
    }
  );
  return response.data;
}

export async function getReportPresentationSessionPerformanceService(
  params: GetReportPresentationSessionPerformanceParams
): Promise<GetReportPresentationSessionPerformanceResponse> {
  const response =
    await instance.get<GetReportPresentationSessionPerformanceResponse>(
      `/Report/Presentation/Analytics/SessionPerformance`,
      {
        params,
      }
    );
  return response.data;
}

export async function getReportPresentationHighestIntentAudienceService(
  params: GetReportPresentationHighestIntentAudienceParams
): Promise<GetReportPresentationHighestIntentAudienceResponse> {
  const response =
    await instance.get<GetReportPresentationHighestIntentAudienceResponse>(
      `/Report/Presentation/Analytics/HighestIntentAudience`,
      {
        params,
      }
    );
  return response.data;
}

export async function getReportPresentationRoiConversionService(
  params: GetReportPresentationRoiConversionParams
): Promise<GetReportPresentationRoiConversionResponse> {
  const response = await instance.get<GetReportPresentationRoiConversionResponse>(
    `/Report/Presentation/Analytics/RoiConversion`,
    {
      params,
    }
  );
  return response.data;
}

export async function getReportPresentationParticipantDeepDiveService(
  params: GetReportPresentationParticipantDeepDiveParams
): Promise<GetReportPresentationParticipantDeepDiveResponse> {
  const response =
    await instance.get<GetReportPresentationParticipantDeepDiveResponse>(
      `/Report/Presentation/Analytics/ParticipantDeepDive`,
      {
        params,
      }
    );
  return response.data;
}

export async function getReportPresentationAnalyticsOverviewService(
  presentationId: string
): Promise<GetReportPresentationAnalyticsOverviewResponse> {
  const response =
    await instance.get<GetReportPresentationAnalyticsOverviewResponse>(
      `/Report/Presentation/Analytics/Overview/${presentationId}`
    );
  return response.data;
}

export async function getReportPresentationAssetPerformanceService(
  presentationId: string
): Promise<GetReportPresentationAssetPerformanceResponse> {
  const response =
    await instance.get<GetReportPresentationAssetPerformanceResponse>(
      `/Report/Presentation/Analytics/Assets/${presentationId}`
    );
  return response.data;
}

export async function getReportPresentationPerformanceInsightService(
  presentationId: string,
  params?: GetReportPresentationPerformanceInsightParams
): Promise<GetReportPresentationPerformanceInsightResponse> {
  const response =
    await instance.get<GetReportPresentationPerformanceInsightResponse>(
      `/Report/Presentation/Analytics/PerformanceInsight/${presentationId}`,
      {
        params,
      }
    );
  return response.data;
}

export async function getReportPresentationPerformanceScoreService(
  presentationId: string
): Promise<GetReportPresentationPerformanceScoreResponse> {
  const response =
    await instance.get<GetReportPresentationPerformanceScoreResponse>(
      `/Report/PerformanceScore/Presentation/${presentationId}`
    );
  return response.data;
}

export async function getReportPerformanceSummaryService(
  presentationId: string
): Promise<GetReportPerformanceSummaryResponse> {
  const response = await instance.get<GetReportPerformanceSummaryResponse>(
    `/Report/PerformanceSummary/${presentationId}`
  );
  return response.data;
}

export async function getReportAttendancePresentationService(
  presentationId: string
): Promise<GetReportAttendancePresentationResponse> {
  const response = await instance.get<GetReportAttendancePresentationResponse>(
    `/Report/Attendance/Presentation/${presentationId}`
  );
  return response.data;
}

export async function getReportPresentationMetricsService(
  presentationId: string
): Promise<GetReportPresentationMetricsResponse> {
  const response = await instance.get<GetReportPresentationMetricsResponse>(
    `/report/PresentationMetrics/${presentationId}`
  );
  return response.data;
}

export async function getReportAverageTimeSpentService(
  presentationId: string
): Promise<GetReportAverageTimeSpentResponse> {
  const response = await instance.get<GetReportAverageTimeSpentResponse>(
    `/Report/AverageTimeSpent/${presentationId}`
  );
  return response.data;
}
